import { Banner } from 'component/Banner/Banner'
import { Button } from 'component/Button'
import { Space } from 'component/Space'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { observer } from 'mobx-react-lite'
import { BrandStep } from 'page/StartScreen/combine/brand/BrandStep'
import { CategoryStep } from 'page/StartScreen/combine/category/CategoryStep'
import { ColorStep } from 'page/StartScreen/combine/color/ColorStep'
import { CombineStep } from 'page/StartScreen/combine/CombineStep'
import { PriceStep } from 'page/StartScreen/combine/price/PriceStep'
import { ReactElement } from 'react'
import { controller } from 'store/controller'
import { store } from 'store/store'

function content(): ReactElement | undefined {
  const item = store.tempComboItem || store.item
  const step = item?.step!
  switch (step) {
    case 'combine':
      return <CombineStep />
    case 'brand':
      return <BrandStep />
    case 'color':
      return <ColorStep />
    case 'category':
      return <CategoryStep />
    case 'price':
      return <PriceStep />
  }
}

function title(): string {
  const item = store.tempComboItem || store.item
  const step = item?.step!
  switch (step) {
    case 'combine':
      return i18n('AddToTheLook')
    case 'brand':
      return i18n('Brand')
    case 'color':
      return i18n('Color')
    case 'category':
      return i18n('Category')
    case 'price':
      return i18n('Price')
    default:
      return ''
  }
}

function bottom() {
  const item = store.tempComboItem || store.item!
  const apply = ['combine'].includes(item.step)
  const show = item.step != 'combine'

  const onContinue = useLatestCallback(() => {
    void controller.generateRec()
  })

  const onShow = useLatestCallback(() => {
    if (item.step === 'brand') item.applyBrand()
    if (item.step === 'color') item.applyColor()
    if (item.step === 'category') item.applyCategory()
    if (item.step === 'price') item.applyPrice()
  })

  if (apply)
    return (
      <Space wide>
        <Button
          large
          wide
          primary
          disabled={!item.rec.item}
          onClick={onContinue}
        >
          {i18n('CombineContinue')}
        </Button>
      </Space>
    )

  if (show)
    return (
      <Space wide>
        <Button large wide primary onClick={onShow}>
          {i18n('ShowItems')}
        </Button>
      </Space>
    )

  return undefined
}

export const CombineBanner = observer(() => {
  const item = store.tempComboItem || store.item!
  const compact = ['combine', 'price'].includes(item.step)

  const beforeClose = useLatestCallback(() => {
    return item.step === 'combine'
  })

  const onCancel = useLatestCallback(() => {
    if (item.step === 'combine') {
      item.step = 'none'
      store.tempComboItem = undefined
      return
    }

    if (item.step === 'brand') item.rec.brand.unapply()
    if (item.step === 'color') item.rec.color.unapply()
    if (item.step === 'category') item.rec.category.unapply()
    if (item.step === 'price') {
      item.rec.min_price.unapply()
      item.rec.max_price.unapply()
    }

    item.step = 'combine'
  })

  return (
    <Banner
      tall={!compact}
      scroll={compact}
      id={item.step}
      title={title()}
      bottom={bottom()}
      beforeClose={beforeClose}
      onCancel={onCancel}
    >
      {content()}
    </Banner>
  )
})
